// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI, userAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch current user data
  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const response = await userAPI.getMe();
        setUser(response.data.user || response.data);
        return response.data.user || response.data;
      } catch (err) {
        console.error('Auth check failed:', err);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setUser(null);
        return null;
      }
    }
    return null;
  }, []);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      await fetchUser();
      setLoading(false);
    };

    checkAuth();
  }, [fetchUser]);

  // Refresh user data (call after profile updates, etc.)
  const refreshUser = useCallback(async () => {
    return await fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login(email, password);
      const { tokens, user: userData } = response.data;

      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      setUser(userData);

      // Fetch full user profile (login response may not include all fields like accountType)
      await fetchUser();

      return { success: true };
    } catch (err) {
      const data = err.response?.data || {};
      const message = data.error || 'Login failed';
      const code = data.code || null;
      setError(message);
      return { success: false, error: message, code, warning: data.warning || null };
    }
  };

  const register = async (name, email, phone, password) => {
    try {
      setError(null);
      const response = await authAPI.register(name, email, phone, password);
      const { tokens, user: userData } = response.data;

      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      setUser(userData);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      const details = err.response?.data?.details || null;
      setError(message);
      return { success: false, error: message, details };
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authAPI.logout(refreshToken);
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    }
  };

  // Set user directly (for auth callback)
  const setUserData = useCallback((userData) => {
    setUser(userData);
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    setUserData,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
